import { carimboDaMensagem, type ItemHistorico, type MensagemChat } from '@mymaxx/shared';
import { Anexo, Botao, Citacao } from '@mymaxx/ui';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, Download, Star } from 'lucide-react';
import { useState } from 'react';
import { ErroApi, clienteApi } from '../lib/api';

/**
 * Um atendimento anterior, dentro da conta.
 *
 * **Só leitura, de propósito.** Protocolo encerrado é o registro do que aconteceu, não uma
 * conversa que continua: reabrir o mesmo fio anos depois embaralharia o histórico e os
 * prazos. Quem precisa falar de novo abre um atendimento novo — o botão está na conta.
 *
 * O PDF continua existindo para quem quer guardar ou anexar a um pedido, mas ler não
 * deveria exigir baixar nada.
 */
export function AtendimentoAnterior({
  item,
  aoVoltar,
}: {
  item: ItemHistorico;
  aoVoltar: () => void;
}) {
  const [erro, setErro] = useState<string | null>(null);
  const [baixando, setBaixando] = useState(false);
  const atendimento = useQuery({
    queryKey: ['atendimento', item.id],
    queryFn: () => clienteApi.atendimento(item.id),
  });

  async function baixar() {
    setBaixando(true);
    setErro(null);
    try {
      await clienteApi.baixarAtendimento(item.id, item.protocolo);
    } catch (e) {
      setErro(e instanceof ErroApi ? e.mensagem : 'Não consegui gerar o PDF.');
    } finally {
      setBaixando(false);
    }
  }

  const dados = atendimento.data;

  return (
    <main className="mx-auto flex min-h-dvh max-w-2xl flex-col px-5 py-6">
      <header className="mb-4 flex items-start gap-3">
        <button
          type="button"
          onClick={aoVoltar}
          aria-label="Voltar para a conta"
          className="mt-0.5 shrink-0 text-neutral-500 hover:text-neutral-800 dark:hover:text-neutral-200"
        >
          <ArrowLeft size={20} />
        </button>
        <div className="min-w-0 flex-1">
          <p className="font-mono text-xs text-neutral-500">{item.protocolo}</p>
          <h1 className="text-lg font-semibold">{item.setor ?? 'Atendimento'}</h1>
          <p className="text-xs text-neutral-500">
            {new Date(item.iniciadaEm).toLocaleString('pt-BR')}
            {dados?.duracaoSeg ? ` · durou ${duracao(dados.duracaoSeg)}` : ''}
            {dados?.atendente ? ` · atendido por ${dados.atendente}` : ''}
          </p>
          {item.pontoServico && <p className="text-xs text-neutral-500">{item.pontoServico}</p>}
        </div>
      </header>

      {dados?.avaliacao !== null && dados?.avaliacao !== undefined && (
        <p className="mb-3 flex items-center gap-1 text-xs text-neutral-500">
          Você avaliou com
          <span className="flex items-center gap-0.5 font-medium text-amber-600 dark:text-amber-400">
            {dados.avaliacao}
            <Star size={11} fill="currentColor" />
          </span>
          {dados.avaliacaoComentario && (
            <span className="italic">“{dados.avaliacaoComentario}”</span>
          )}
        </p>
      )}

      {atendimento.isPending && <p className="text-sm text-neutral-500">Carregando a conversa…</p>}
      {atendimento.isError && (
        <p className="text-sm text-red-600">Não consegui carregar este atendimento.</p>
      )}

      <div className="flex-1 space-y-2 rounded-card bg-neutral-50 p-4 dark:bg-neutral-950">
        {dados?.mensagens.map((m) => (
          <Bolha key={m.id} mensagem={m} />
        ))}
      </div>

      {erro && <p className="mt-3 text-sm text-red-600">{erro}</p>}

      <div className="mt-4 flex flex-wrap gap-2">
        <Botao variante="secundario" carregando={baixando} onClick={() => void baixar()}>
          <Download size={16} /> Baixar em PDF
        </Botao>
        <Botao variante="secundario" onClick={aoVoltar}>
          Voltar
        </Botao>
      </div>
    </main>
  );
}

/** A mesma bolha do chat, sem o botão de responder: aqui não se responde. */
function Bolha({ mensagem }: { mensagem: MensagemChat }) {
  if (mensagem.de === 'sistema') {
    return (
      <p className="mx-auto max-w-[90%] rounded bg-neutral-200 px-2 py-1 text-center text-[11px] text-neutral-600 dark:bg-neutral-800 dark:text-neutral-300">
        {mensagem.texto}
      </p>
    );
  }

  const doCliente = mensagem.de === 'cliente';
  return (
    <div
      className={`max-w-[85%] rounded-2xl px-3 py-2 text-sm whitespace-pre-line shadow-sm ${
        doCliente
          ? 'ml-auto rounded-br-sm bg-marca-600 text-white'
          : 'mr-auto rounded-bl-sm bg-white dark:bg-neutral-800'
      }`}
    >
      {mensagem.autorNome && !doCliente && (
        <p className="mb-0.5 text-[11px] font-medium opacity-70">{mensagem.autorNome}</p>
      )}
      {mensagem.respondeA && (
        <Citacao
          autor={
            mensagem.respondeA.de === 'cliente'
              ? 'Você'
              : (mensagem.respondeA.autorNome ?? 'Atendimento')
          }
          resumo={mensagem.respondeA.resumo}
        />
      )}
      {mensagem.midia && (
        <Anexo
          url={mensagem.midia.url}
          nomeOriginal={mensagem.midia.nomeOriginal}
          mimeType={mensagem.midia.mimeType}
          // Cliente e técnico estão quase sempre no celular: baixar sozinho joga o
          // arquivo numa pasta que ninguém procura depois.
          perguntarOndeSalvar
        />
      )}
      {mensagem.texto}
      <span className="ml-2 align-bottom text-[10px] opacity-60">
        {carimboDaMensagem(mensagem.criadaEm)}
      </span>
    </div>
  );
}

function duracao(segundos: number): string {
  const min = Math.round(segundos / 60);
  if (min < 60) return `${min} min`;
  return `${Math.floor(min / 60)}h ${String(min % 60).padStart(2, '0')}min`;
}
